import {
  ChecklistRunDetail,
  ChecklistRunEvidence,
  ChecklistRunItem,
  ChecklistRunSummary,
  ChecklistTemplate,
} from '@/features/checklists/types';
import type { Citation } from '@/features/documents/types';

const matchesTimestamp = (value: string | null, snapshot: unknown) =>
  value === snapshot ||
  (typeof value === 'string' &&
    typeof snapshot === 'string' &&
    Date.parse(value) === Date.parse(snapshot));

export const matchesChecklistSnapshot = (
  rows: unknown,
  runs: ChecklistRunSummary[] | undefined,
) => {
  if (!runs || !Array.isArray(rows) || rows.length !== runs.length) return false;

  const remaining = new Map(runs.map((run) => [run.id, run]));
  return rows.every((row) => {
    if (!row || typeof row !== 'object') return false;
    const run = remaining.get(row.run_id);
    if (!run) return false;
    remaining.delete(row.run_id);

    return (
      run.name === row.name &&
      run.status === row.status &&
      run.progressPercent === row.progress &&
      run.workPackageId === row.work_package_id &&
      run.error === row.error &&
      matchesTimestamp(run.createdAt, row.created_at) &&
      matchesTimestamp(run.startedAt, row.started_at) &&
      matchesTimestamp(run.completedAt, row.completed_at)
    );
  });
};

export const withSortedItems = (run: ChecklistRunDetail) => ({
  ...run,
  items: run.items.toSorted((a, b) => a.itemIndex - b.itemIndex),
});

export const isVolveTemplate = (template: ChecklistTemplate) => template.createdBy === null;

export const checklistItemVerdict = (item: ChecklistRunItem) =>
  item.verdict === 'uncertain' && item.evidence.length === 0 ? 'noFinding' : item.verdict;

export const checklistEvidenceCitations = (evidence: ChecklistRunEvidence[]): Citation[] =>
  evidence.map((entry, index) => ({
    chunkId: entry.chunkId,
    content: entry.snippet ?? '',
    title: entry.documentName ?? '',
    url: entry.docId ?? '',
    filepath: entry.docId ?? '',
    postfix: index + 1,
    order: index,
    pageStart: entry.pageStart ?? undefined,
    pageEnd: entry.pageEnd ?? undefined,
  }));

export const isChecklistItemActive = (item: ChecklistRunItem) =>
  item.status === 'pending' || item.status === 'running';
